/**
 * Access request / marketing lead submissions from the public website
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Campaign as CampaignIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { adminService } from '../../services/adminService';
import { MarketingLead, MarketingLeadFilters } from '../../types/admin';
import { formatDate } from '../../utils/formatters';

const MarketingLeads: React.FC = () => {
  const [leads, setLeads] = useState<MarketingLead[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState<MarketingLeadFilters>({
    page: 1,
    page_size: 25,
  });
  const [emailFilter, setEmailFilter] = useState('');
  const [selectedLead, setSelectedLead] = useState<MarketingLead | null>(null);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminService.getMarketingLeads({
        ...filters,
        email: emailFilter || undefined,
      });
      setLeads(result.leads);
      setTotalCount(result.total_count);
    } catch (err: any) {
      setError(err?.message || 'Failed to load access requests');
    } finally {
      setLoading(false);
    }
  }, [filters, emailFilter]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadLeads();
    } finally {
      setRefreshing(false);
    }
  };

  const pageCount = Math.max(1, Math.ceil(totalCount / (filters.page_size ?? 25)));

  const displayName = (lead: MarketingLead) => {
    const parts = [lead.first_name, lead.last_name].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : '—';
  };

  const signalSource = (lead: MarketingLead) => {
    const extra = lead.extra;
    if (!extra) return '—';
    if (typeof extra.signal_source === 'string') return extra.signal_source;
    return '—';
  };

  const extraText = (lead: MarketingLead, key: string) => {
    const value = lead.extra?.[key];
    if (typeof value === 'string' && value.trim()) return value;
    return '—';
  };

  if (loading && leads.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" display="flex" alignItems="center" gap={1}>
          <CampaignIcon />
          Access Requests
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={handleRefresh} disabled={refreshing} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Submissions from the public website, including early-access requests and tier-quiz results.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Filter by email"
                value={emailFilter}
                onChange={(e) => {
                  setEmailFilter(e.target.value);
                  setFilters((f) => ({ ...f, page: 1 }));
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="body2" color="text.secondary">
                {totalCount} submission{totalCount === 1 ? '' : 's'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Submitted</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Signal source</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Recommended tier</TableCell>
              <TableCell>Message</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>
                    No access requests yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              leads.map((lead) => (
                <TableRow
                  key={lead.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setSelectedLead(lead)}
                >
                  <TableCell>{formatDate(lead.created_at)}</TableCell>
                  <TableCell>{displayName(lead)}</TableCell>
                  <TableCell>{lead.email}</TableCell>
                  <TableCell>{signalSource(lead)}</TableCell>
                  <TableCell>{lead.source || '—'}</TableCell>
                  <TableCell>{extraText(lead, 'recommended_tier_name')}</TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 280,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {lead.message || '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pageCount > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={pageCount}
            page={filters.page ?? 1}
            onChange={(_, page) => setFilters((f) => ({ ...f, page }))}
            color="primary"
          />
        </Box>
      )}

      <Dialog
        open={Boolean(selectedLead)}
        onClose={() => setSelectedLead(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedLead && (
          <>
            <DialogTitle>Access request — {selectedLead.email}</DialogTitle>
            <DialogContent dividers>
              <Typography variant="subtitle2" gutterBottom>
                Submitted {formatDate(selectedLead.created_at)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Name:</strong> {displayName(selectedLead)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Email:</strong> {selectedLead.email}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Signal source:</strong> {signalSource(selectedLead)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Source:</strong> {selectedLead.source || '—'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Recommended tier:</strong> {extraText(selectedLead, 'recommended_tier_name')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Marketing updates:</strong> {extraText(selectedLead, 'marketing_opt_in')}
              </Typography>
              {selectedLead.client_ip && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>IP:</strong> {selectedLead.client_ip}
                </Typography>
              )}
              {selectedLead.referrer && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Referrer:</strong> {selectedLead.referrer}
                </Typography>
              )}
              {selectedLead.message && (
                <Typography variant="body2" sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>
                  <strong>Message:</strong>
                  {'\n'}
                  {selectedLead.message}
                </Typography>
              )}
              {selectedLead.extra && Object.keys(selectedLead.extra).length > 0 && (
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{ mt: 2, fontSize: 12, overflow: 'auto' }}
                >
                  {JSON.stringify(selectedLead.extra, null, 2)}
                </Typography>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default MarketingLeads;
